import React, { useCallback, useMemo, useRef } from 'react';
import { CLIENT_TYPES } from '@medflex/utils/constants';
import { IconShareAlt } from '@medflex/components/stories/Icons';
import InactiveChatRemark from '@medflex/components/stories/InactiveChatRemark';
import { useTranslation } from 'react-i18next';
import { trackCustomEvent, trackEvent } from '@medflex/utils/Tracking';
import config from '@medflex/utils/config';
import isSelf from '@medflex/utils/isSelf';
import { FEATURE_KEYS } from '@medflex/utils/subscriptionEnums';
import isPatientUserId from '@medflex/utils/isPatientUserId';
import PhysicianStatusBar from '@medflex/components/stories/PhysicianStatusBar/PhysicianStatusBar';
import { DocumentIcon } from '@heroicons/react/24/solid';
import AbsenceInfoBanner from '@medflex/components/stories/AbsenceInfoBanner';
import { isAbsenceActive } from '@medflex/shared-utils';
import useFeatures from '../../hooks/useFeatures';
import useSelectedMainContact from '../../hooks/useSelectedMainContact';
import ChatStreamModule from '../ChatStreamModule';
import useSelectedContact from '../../hooks/useSelectedContact';
import ComposerAreaModule from '../ComposerAreaModule';
import isSupportPatient from '../../../utils/isSupportPatient';
import useSnippets from '../../hooks/useSnippets';
import usePermissions from '../../hooks/usePermissions';
import useModal from '../../hooks/useModal';
import useIsSelectedContactPhysician from '../../hooks/useIsSelectedContactPhysician';
import useMessages from '../../hooks/useMessages';
import { DEMO_BOOKING_URL } from '../../constants';
import usePhysicianStatus from '../../hooks/usePhysicianStatus';
import { MODALS } from '../../hooks/useIsModal';
import TitleBar from './TitleBar';
import useUserProfile from '../../hooks/useUserProfile';
import useSubscription from '../../hooks/useSubscription';
import useExternalServices, { EXTERNAL_SERVICES } from '../../hooks/useExternalServices';

const sortByTitle = (a, b) => {
  if (a.title.toLowerCase() < b.title.toLowerCase()) return -1;
  if (b.title.toLowerCase() < a.title.toLowerCase()) return 1;
  return 0;
};

const PhysicianChatRoomModule = () => {
  const chatRoomRef = useRef();
  const { selectedContact } = useSelectedContact();
  const { open } = useModal();
  const { t } = useTranslation('physician');
  const { send, isSending, errors } = useMessages();
  const { snippets } = useSnippets();
  const isSelfContact = selectedContact && isSelf(selectedContact?.user);
  const { status } = usePhysicianStatus(
    selectedContact?.id && !isSelfContact && !isSupportPatient({ id: selectedContact.user.id })
      ? [selectedContact.user.id]
      : [],
  );
  const isSelectedContactPhysician = useIsSelectedContactPhysician();
  const selectedMainContact = useSelectedMainContact();
  const physicianStatus = status?.[selectedContact?.user.id];

  const { hasPatientAccess, hasPhysicianAccess } = usePermissions();
  const [chatCharacters] = useFeatures([FEATURE_KEYS.CHAT_CHARACTERS]);
  const { getQueryString } = useModal();
  const { userProfile } = useUserProfile();
  const { postalCode, email, lastName, workplace, chatSettings } = userProfile.attributes;
  const { isBasicUser } = useSubscription();
  const [simpleprax] = useExternalServices([EXTERNAL_SERVICES.SIMPLEPRAX]);

  const shouldShowChatDisabledInfo =
    selectedContact?.type === 'patient' && !chatSettings.patient.canSendMessages;

  const actionItems = useMemo(
    () =>
      [
        isSelectedContactPhysician &&
          hasPatientAccess &&
          hasPhysicianAccess &&
          !isSelfContact && {
            text: t('actions.share_patient_contact'),
            icon: <IconShareAlt size="text" />,
            onClick: event => {
              trackEvent(event);
              open({
                modal: 'sharePatientContact',
                filter: 'patients',
                recipient: selectedContact.user.id,
              });
            },
          },
        simpleprax?.active && {
          text: t('global:actions.startQuestionnaire'),
          icon: <DocumentIcon className="w-text h-text text-blue-500" />,
          onClick: () =>
            open({
              modal: MODALS.NEW_QUESTIONNAIRE,
              userId: selectedContact.user.id,
              tab: 'groups',
            }),
        },
      ].filter(Boolean),
    [
      selectedContact,
      hasPatientAccess,
      hasPhysicianAccess,
      isSelectedContactPhysician,
      isSelfContact,
      simpleprax?.active,
      t,
      open,
    ],
  );

  const handleScheduleDemo = useCallback(
    event => {
      trackEvent(event);
      const params = new URLSearchParams();

      const paramObject = {
        zip: postalCode,
        email,
        lastname: lastName,
        company: workplace,
      };

      Object.entries(paramObject).forEach(([key, value]) => {
        if (value) {
          params.append(key, value);
        }
      });

      const url = new URL(DEMO_BOOKING_URL);
      url.search = params.toString();

      window.open(url, '_blank', 'noreferrer, noopener');
    },
    [email, lastName, postalCode, workplace],
  );

  const handleTrackingStatusBar = () => {
    trackCustomEvent({
      eventCategory: 'user interactions',
      eventAction: 'open',
      eventName: 'Arzt_Hinweis_bar | physician',
    });
  };

  if (!selectedContact) return null;
  return (
    <div className="flex flex-col flex-auto h-full w-full overflow-hidden" ref={chatRoomRef}>
      <TitleBar contact={selectedContact} />
      {physicianStatus?.status && !physicianStatus.statusDisplayed && (
        <PhysicianStatusBar
          text={physicianStatus.status}
          detailsUrl={getQueryString({ modal: MODALS.PHYSICIAN_STATUS_MODAL })}
          onClick={handleTrackingStatusBar}
        />
      )}
      <div
        data-component="ChatRoom"
        className="flex flex-col h-full bg-slate-100 border-t  border-t-slate-200 flex-auto"
        ref={chatRoomRef}
      >
        {selectedContact.user.deactivated && (
          <InactiveChatRemark>
            {t('messenger:PhysicianChatRoomModule.inactiveUser', {
              user: selectedContact.user.fullNameWithTitle,
            })}
          </InactiveChatRemark>
        )}
        <ChatStreamModule onScheduleDemo={handleScheduleDemo} isBasicUser={isBasicUser} />
        {physicianStatus?.absence && (
          <AbsenceInfoBanner
            className="p-5"
            startAt={physicianStatus.absence.startAt}
            endAt={physicianStatus.absence.endAt}
          >
            {physicianStatus.absence.note}
          </AbsenceInfoBanner>
        )}
        {selectedContact.enabled &&
          !(
            isAbsenceActive(physicianStatus?.absence) &&
            !physicianStatus?.absence.allowNotifications
          ) && (
            <ComposerAreaModule
              calcComposerMaxRows
              actionItems={actionItems}
              snippets={snippets.sort(sortByTitle)}
              dropTarget={chatRoomRef}
              id={selectedContact.id}
              sendMessage={send}
              isSendingMessage={isSending}
              errors={errors}
              userId={selectedContact.user.id}
              mainContactUserId={selectedMainContact && selectedMainContact.user.id}
              chatCharacters={
                isPatientUserId(selectedContact.user.id) &&
                !isSupportPatient(selectedContact.user) &&
                chatCharacters
              }
              preventAudioRecording={config.CLIENT_TYPE === CLIENT_TYPES.CHAT}
              shouldShowChatDisabledInfo={shouldShowChatDisabledInfo}
            />
          )}
      </div>
    </div>
  );
};

PhysicianChatRoomModule.propTypes = {};

export default PhysicianChatRoomModule;
